import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { date, money, todayYmd } from '../lib/format';
import { useContacts } from '../lib/contacts-api';
import {
  DOCUMENT_TYPE_LABELS,
  SALES_DOCUMENT_TYPES,
  SALES_INVOICE_STATUSES,
  STATUS_LABELS,
  STATUS_TONES,
  invoiceErrorMessage,
  useInvoices,
  type ListInvoicesParams,
  type SalesDocumentType,
  type SalesInvoice,
  type SalesInvoiceStatus,
} from '../lib/invoices-api';
import { usePostDocument } from '../lib/accounting-api';
import { errorMessage } from '../lib/api-error';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * Satış belgeleri listesi — fatura, proforma ve kredi notu TEK tabloda.
 *
 * ⚠ TASLAK İLE DÜZENLENMİŞ BELGE GÖRSEL OLARAK AYRIDIR ve bu kozmetik bir
 * tercih değil:
 *   · Taslağın NUMARASI YOKTUR (numara yalnız düzenlemede tahsis edilir) ve
 *     hiçbir KDV dönemine düşmez — yani mali bir belge DEĞİLDİR.
 *   · Düzenlenmiş belge DEĞİŞMEZ ve kesintisiz dizinin parçasıdır.
 * İkisi aynı görünseydi operatör "şu faturayı düzeltirim" diye düşünüp
 * düzeltilemeyecek bir belgeye tıklardı. Taslak satırı bu yüzden kesik
 * çerçeveli, soluk ve "numarasız taslak" yazılı; düzenlenmiş belge mono
 * numarasıyla koyu.
 */

const PAGE_SIZE = 25;

type StatusFilter = SalesInvoiceStatus | 'all';
type TypeFilter = SalesDocumentType | 'all';

export function InvoicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const readOnly = isReadOnly(user);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [documentType, setDocumentType] = useState<TypeFilter>('all');
  const [contactId, setContactId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  /*
   * Süzgeç değişince İLK SAYFAYA dönülür. Aksi hâlde 3. sayfadayken süzgeci
   * daraltmak "sonuç yok" gösterir — oysa sonuç vardır, sadece 3. sayfada
   * değildir; operatör süzgecin bozuk olduğunu sanır.
   */
  useEffect(() => {
    setPage(1);
  }, [status, documentType, contactId, from, to]);

  /*
   * Cari süzgeci için müşteri listesi. Tek operatörlü bir işletmede cari sayısı
   * üç haneyi geçmez; ayrı bir arama kutusu yerine düz bir seçim listesi hem
   * daha hızlı hem de yanlış cariye tıklamayı zorlaştırır.
   */
  const customers = useContacts({ limit: 200, isCustomer: true, active: true });

  const params: ListInvoicesParams = {
    page,
    pageSize: PAGE_SIZE,
    ...(status === 'all' ? {} : { status }),
    ...(documentType === 'all' ? {} : { documentType }),
    ...(contactId ? { contactId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const list = useInvoices(params);
  const data = list.data ?? null;

  const total = data?.total ?? 0;
  const shownFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const shownTo = data ? (page - 1) * PAGE_SIZE + data.items.length : 0;
  const hasPrev = page > 1;
  const hasNext = data !== null && shownTo < total;

  return (
    <>
      <PageHeader
        title="Satış belgeleri"
        description="Taslak numarasızdır ve hiçbir KDV dönemine düşmez; düzenlenmiş belge değişmez ve kesintisiz dizinin parçasıdır."
        actions={
          !readOnly && <Button onClick={() => void navigate('/invoices/new')}>Yeni taslak</Button>
        }
      />

      <Card className="mb-4">
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-5">
          <Select
            label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          >
            <option value="all">Tümü</option>
            {SALES_INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </Select>

          <Select
            label="Belge türü"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as TypeFilter)}
          >
            <option value="all">Tümü</option>
            {SALES_DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {DOCUMENT_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>

          <Select
            label="Cari"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            hint={customers.isPending ? 'Müşteriler yükleniyor…' : undefined}
          >
            <option value="">Tümü</option>
            {(customers.data?.items ?? []).map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.name}
              </option>
            ))}
          </Select>

          {/*
           * Aralık `issueDate` üzerindedir ve DAHİLDİR. ⚠ KDV dönemi bu tarihe
           * değil `vatChargeableAt`e bağlıdır (kasa esasında ikisi aylarca
           * ayrışır); süzgeç bu yüzden "düzenleme tarihi" diye adlandırılıyor.
           */}
          <Input
            label="Düzenleme tarihi ≥"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <Input
            label="Düzenleme tarihi ≤"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        {list.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner size={24} />
          </div>
        ) : list.isError ? (
          <ErrorState message={invoiceErrorMessage(list.error)} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="Belge yok"
            description="Seçili süzgeçlerle eşleşen bir satış belgesi bulunamadı."
          />
        ) : (
          <>
            <div className={list.isFetching ? 'opacity-60 transition-opacity' : undefined}>
              <Table>
                <thead>
                  <tr>
                    <Th>Numara</Th>
                    <Th>Tarih</Th>
                    <Th>Müşteri</Th>
                    <Th align="right">Tutar</Th>
                    <Th>Durum</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((invoice) => (
                    <InvoiceRow key={invoice.id} invoice={invoice} />
                  ))}
                </tbody>
              </Table>
            </div>

            <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--c-border)] px-4 py-3">
              <span className="text-xs text-[var(--c-ink-soft)]">
                {shownFrom}–{shownTo} / {total} belge
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!hasPrev}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Önceki
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!hasNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Sonraki
                </Button>
              </div>
            </footer>
          </>
        )}
      </Card>
    </>
  );
}

/**
 * Bir satır.
 *
 * ⚠ TUTAR `money()`den geçer ve HİÇBİR yerde toplanmaz: sunucunun döndürdüğü
 * `totalInclVat` olduğu gibi gösterilir. Listede "seçili satırların toplamı"
 * gibi bir hesap yapmak, kredi notunun işaretini (belge türünde saklanır,
 * tutarda değil) ters çevirir ve ciroyu şişirirdi.
 */
function InvoiceRow({ invoice }: { invoice: SalesInvoice }) {
  const draft = invoice.status === 'DRAFT';
  const postToLedger = usePostDocument();
  const [error, setError] = useState<string | null>(null);

  return (
    <tr className={draft ? 'bg-[var(--c-surface-alt)]/60' : undefined}>
      <Td className={draft ? 'border-l-2 border-l-[var(--c-border-strong)]' : undefined}>
        <Link
          to={`/invoices/${invoice.id}`}
          className="font-medium text-[var(--c-brand)] hover:underline"
        >
          {invoice.documentNumber ? (
            <span className="font-mono text-[13px]">{invoice.documentNumber}</span>
          ) : (
            <span className="italic text-[var(--c-ink-soft)]">numarasız taslak</span>
          )}
        </Link>
        <span className="block text-xs text-[var(--c-ink-faint)]">
          {DOCUMENT_TYPE_LABELS[invoice.documentType]}
          {invoice.correctsDocumentNumber && ` → ${invoice.correctsDocumentNumber}`}
        </span>
      </Td>

      <Td>
        {date(invoice.issueDate)}
        {/*
         * Vade, tarihin altında: gecikmiş belgeyi listede görmek, tahsilat
         * takibinin tamamıdır. `OVERDUE` sunucuda bir DURUM DEĞİLDİR
         * (türetilebilir olduğu için kolona yazılmadı) — burada da öyle.
         */}
        <span className="block text-xs text-[var(--c-ink-faint)]">
          vade {date(invoice.dueDate)}
        </span>
      </Td>

      <Td>
        <span className="block">{invoice.billingName}</span>
        {invoice.billingVatNumber && (
          <span className="block font-mono text-xs text-[var(--c-ink-faint)]">
            {invoice.billingVatNumber}
          </span>
        )}
      </Td>

      <Td align="right">
        <span className="font-medium">{money(invoice.totalInclVat)}</span>
        <span className="block text-xs text-[var(--c-ink-faint)]">
          KDV {money(invoice.vatAmount)}
        </span>
      </Td>

      <Td>
        <div className="flex flex-wrap gap-1">
          <Badge tone={STATUS_TONES[invoice.status]}>{STATUS_LABELS[invoice.status]}</Badge>
          {isOverdue(invoice) && <Badge tone="danger">gecikmiş</Badge>}
          {invoice.documentType === 'CREDIT_NOTE' && <Badge tone="warn">düzeltme</Badge>}
          {invoice.documentType === 'PROFORMA' && <Badge tone="neutral">mali değil</Badge>}
        </div>
        {/*
          ⚠ "DEFTERE KAYDET" AYRI BİR ADIM ve fatura kesmekten otomatik akmıyor.
          Sunucuda kayıt, `issue()` transaction'ının DIŞINDA: o transaction
          numara sayacının satır kilidini COMMIT'e kadar tutuyor ve oraya İKİNCİ
          bir sayaç (yevmiye numarası) sokmak deadlock riski açardı. Ayrıca
          kayıt hatası KESİLMİŞ faturayı geri sardırırdı — oysa kesilmiş fatura
          yasal olarak vardır ve kaydının gecikmesi onu yok etmez.

          ⚠ PROFORMA'DA DÜĞME YOK: proforma fatura değildir, KDV doğurmaz ve
          deftere hiç girmez.
        */}
        {!draft && invoice.documentType !== 'PROFORMA' && (
          <div className="mt-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={postToLedger.isPending}
              onClick={() => {
                setError(null);
                postToLedger
                  .mutateAsync({ sourceType: 'SALES_INVOICE', sourceId: invoice.id })
                  .catch((e: unknown) => setError(errorMessage(e)));
              }}
            >
              Deftere kaydet
            </Button>
            {error && <p className="text-xs text-[var(--c-danger)]">{error}</p>}
          </div>
        )}
      </Td>
    </tr>
  );
}

/**
 * Gecikmiş mi?
 *
 * ⚠ TUTAR KARŞILAŞTIRMASI YOK. "Ödenmemiş" bilgisi zaten DURUMDA: `PAID` ve
 * `CREDITED` dışarıda bırakılınca kalan üç durum tanımı gereği açık bakiyedir.
 * `amountPaid < totalInclVat` yazmak, para metnini sayıya çevirmek demek olurdu
 * ve panelin hiçbir yerinde para sayıya çevrilmez.
 *
 * Karşılaştırma Brüksel gününe göre: `todayYmd()` saat dilimi tuzağını
 * (UTC gününe kayma) zaten kapatıyor ve ISO tarihleri metin olarak sıralanır.
 */
function isOverdue(invoice: SalesInvoice): boolean {
  const open: SalesInvoiceStatus[] = ['ISSUED', 'SENT', 'PARTIALLY_PAID'];
  return open.includes(invoice.status) && invoice.dueDate < todayYmd();
}
